import { showNotification } from "components/v2/notification/notification";
import { useCallback, useMemo, useRef, useState } from "react";
import { createScheduler, createWorker, PSM } from "tesseract.js";
import { downloadFile } from "utils/thumbnail-editor";

import OCRContext from "./context";
import {
  drawRectanglesOnGrayscale,
  extractFrameFromHLSStream,
  preprocessFrameBinaryThreshold,
  preprocessFrameGrayscale,
} from "./utils";

const CHAR_WHITELIST = {
  numeric: "0123456789:.-+",
  alphabetic: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  alphanumeric: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789:.-+",
};

const DEFAULT_OCR_OPTIONS = {
  tessedit_char_whitelist: CHAR_WHITELIST.alphanumeric,
  tessedit_pageseg_mode: PSM.SINGLE_LINE,
  tessedit_enable_dict_correction: "0",
  classify_bln_numeric_mode: "1",
  tessedit_char_blacklist: "",
  language_model_penalty_non_dict_word: "0",
  language_model_penalty_non_freq_dict_word: "0",
};

const WORKER_COUNT = 2;

export const OCRProvider = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const workerRef = useRef(null);

  const schedulerRef = useRef(null);
  const workersRef = useRef([]);

  const workerCount = useMemo(() => {
    return Number(localStorage.getItem("OCRWorkerCount")) || WORKER_COUNT;
  }, []);

  const getWorker = useCallback(async () => {
    if (!workerRef?.current) {
      const worker = await createWorker("eng", 1, {
        logger: (m) => {
          console.log(m);
        },
        errorHandler: (err) => console.error(err),
        config: {
          ...DEFAULT_OCR_OPTIONS,
        },
      });
      if (!worker) {
        throw new Error("Failed to create Tesseract worker");
      }
      workerRef.current = worker;
    }
    return workerRef.current;
  }, []);

  // eslint-disable-next-line unused-imports/no-unused-vars
  const processUsingWorker = useCallback(
    async ({ frameCanvasDataURL, coordinates, ocrOptions, enableLogging = false }) => {
      const worker = await getWorker();

      const processedBoxes = [];

      for (let i = 0; i < coordinates.length; i++) {
        const box = coordinates[i];

        const boxTiming = {
          name: box.name,
          ocr: 0,
        };

        try {
          // Calculate scaled rectangle coordinates
          const rectangle = {
            left: box.x,
            top: box.y,
            width: box.width,
            height: box.height,
          };

          const ocrStart = performance.now();

          await worker.setParameters({
            ...ocrOptions,
            tessedit_char_whitelist: CHAR_WHITELIST[box.type] || CHAR_WHITELIST.alphanumeric,
          });

          const result = await worker.recognize(frameCanvasDataURL, { rectangle });

          const recognizedText = result.data.text.trim();

          const ocrEnd = performance.now();

          boxTiming.ocr = ocrEnd - ocrStart;

          const output = {
            value: recognizedText,
            name: box.name,
          };

          if (enableLogging) {
            console.log(`  [${box.name}] ✅ Result: ${JSON.stringify(output)}`);
            console.log(`  [${box.name}] ⏱️  Total: ${boxTiming.ocr.toFixed(2)}ms`);
          }

          processedBoxes.push({
            result: output,
            timing: boxTiming,
          });
        } catch (error) {
          boxTiming.error = error.message;

          processedBoxes.push({
            result: {
              value: "",
              name: box.name,
              error: error.message,
            },
            timing: boxTiming,
          });
        }
      }

      return processedBoxes;
    },
    [getWorker],
  );

  const getScheduler = useCallback(async () => {
    if (!schedulerRef?.current) {
      const scheduler = createScheduler();

      const workers = await Promise.all(
        Array.from({ length: workerCount }, async () => {
          return await createWorker("eng", 1, {
            logger: (m) => {
              console.log(m);
            },
            errorHandler: (err) => console.error(err),
            config: {
              ...DEFAULT_OCR_OPTIONS,
            },
          });
        }),
      );

      workers.forEach((worker) => {
        scheduler.addWorker(worker);
      });

      workersRef.current = workers;
      schedulerRef.current = scheduler;
    }
    return schedulerRef.current;
  }, [workerCount]);

  const processUsingScheduler = useCallback(
    async ({ frameCanvasDataURL, coordinates, ocrOptions, enableLogging = false }) => {
      const scheduler = await getScheduler();
      const processedBoxes = [];
      await Promise.all(
        coordinates.map(async (box) => {
          const boxTiming = {
            name: box.name,
            ocr: 0,
          };
          try {
            // const croppedFrame = getCroppedFrame(frameCanvas, box);
            // const croppedFrameDataURL = croppedFrame.toDataURL("image/png");

            // if (enableLogging) {
            //   downloadFile(croppedFrameDataURL, "png");
            // }

            // Calculate scaled rectangle coordinates
            const rectangle = {
              left: box.x,
              top: box.y,
              width: box.width,
              height: box.height,
            };

            const ocrStart = performance.now();

            const result = await scheduler.addJob("recognize", frameCanvasDataURL, {
              ...ocrOptions,
              rectangle,
              tessedit_char_whitelist: CHAR_WHITELIST[box.type] || CHAR_WHITELIST.aplhanumeric,
            });
            const recognizedText = result.data.text.trim();

            const ocrEnd = performance.now();
            boxTiming.ocr = ocrEnd - ocrStart;
            const output = {
              value: recognizedText,
              name: box.name,
            };
            if (enableLogging) {
              console.log(`  [${box.name}] ✅ Result: ${JSON.stringify(output)}`);
              console.log(`  [${box.name}] ⏱️  Total: ${boxTiming.ocr.toFixed(2)}ms`);
            }
            processedBoxes.push({
              result: output,
              timing: boxTiming,
            });
          } catch (error) {
            boxTiming.error = error.message;
            processedBoxes.push({
              result: {
                value: "",
                name: box.name,
                error: error.message,
              },
              timing: boxTiming,
            });
          }
        }),
      );
      return processedBoxes;
    },
    [getScheduler],
  );

  const runOCR = useCallback(
    async ({
      videoUrl,
      targetTime,
      coordinates,
      ocrOptions = {},
      enableLogging = false,
      downloadDebugImage = false,
    }) => {
      if (!videoUrl) {
        throw new Error("Video URL is required");
      }

      if (!targetTime || targetTime <= 0) {
        throw new Error("Invalid target time");
      }

      if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
        throw new Error("Coordinates array is required");
      }

      setIsProcessing(true);
      let frameCanvas = null;
      let worker = null;

      const overallStartTime = performance.now();
      const performanceTimings = {
        frameExtraction: 0,
        preprocessing: 0,
        parallelProcessing: 0,
        boxes: [],
        total: 0,
      };

      try {
        // Extract frame
        const frameExtractionStart = performance.now();
        frameCanvas = await extractFrameFromHLSStream({ videoUrl, targetTime, enableLogging });
        const frameExtractionEnd = performance.now();
        performanceTimings.frameExtraction = frameExtractionEnd - frameExtractionStart;

        const preprocessStart = performance.now();

        const preprocessTimings = {};

        const grayscaleStart = performance.now();
        if (false) {
          frameCanvas = preprocessFrameGrayscale(frameCanvas);
        }
        const grayscaleEnd = performance.now();
        preprocessTimings.grayscale = grayscaleEnd - grayscaleStart;

        const binaryThresholdStart = performance.now();
        if (false) {
          frameCanvas = preprocessFrameBinaryThreshold(frameCanvas, coordinates);
        }
        const binaryThresholdEnd = performance.now();
        preprocessTimings.binaryThreshold = binaryThresholdEnd - binaryThresholdStart;

        const preprocessEnd = performance.now();

        if (enableLogging) {
          console.log("🎨 Preprocessing timings:");
          console.log("🎨 Grayscale: ", preprocessTimings.grayscale.toFixed(2) + "ms");
          console.log("🎨 Binary Threshold: ", preprocessTimings.binaryThreshold.toFixed(2) + "ms");
        }

        performanceTimings.preprocessing = preprocessEnd - preprocessStart;

        if (downloadDebugImage) {
          // Draw rectangles on grayscale image for logging
          const canvasWithRectangles = drawRectanglesOnGrayscale(frameCanvas, coordinates);
          const grayscaleFrameDataURL = canvasWithRectangles.toDataURL("image/png");
          downloadFile(grayscaleFrameDataURL, "png");
          canvasWithRectangles.remove(); // Clean up
        }

        // Convert grayscale canvas to data URL for Tesseract
        const frameCanvasDataURL = frameCanvas.toDataURL("image/png");

        // const processedBoxes = await processUsingWorker({
        //   frameCanvasDataURL,
        //   coordinates,
        //   ocrOptions,
        //   enableLogging,
        // });

        const processedBoxes = await processUsingScheduler({
          frameCanvasDataURL,
          coordinates,
          ocrOptions,
          enableLogging,
        });

        const ocrResults = processedBoxes.map((box) => box.result);

        performanceTimings.boxes = processedBoxes.map((box) => box.timing);

        // Calculate total time
        const overallEndTime = performance.now();
        performanceTimings.total = overallEndTime - overallStartTime;

        // Log performance summary
        if (enableLogging) {
          console.log("\n" + "=".repeat(60));
          console.log("⏱️  PERFORMANCE SUMMARY");
          console.log("=".repeat(60));
          console.log(`\n📹 Frame Extraction: ${performanceTimings.frameExtraction.toFixed(2)}ms`);
          console.log(`🎨 Frame Preprocessing (grayscale): ${performanceTimings.preprocessing.toFixed(2)}ms`);

          let totalOcrTime = 0;

          performanceTimings.boxes.forEach((boxTiming, index) => {
            console.log(`\n  Rectangle ${index + 1} (${boxTiming.name}):`);
            console.log(`    🔤 OCR:        ${boxTiming.ocr.toFixed(2)}ms`);
            if (boxTiming.error) {
              console.log(`    ❌ Error:      ${boxTiming.error}`);
            }

            totalOcrTime += boxTiming.ocr;
          });

          console.log(`\n📊 Aggregated:`);
          console.log(`    Total OCR Time:        ${totalOcrTime.toFixed(2)}ms`);

          console.log(
            `\n🎯 TOTAL PROCESS TIME: ${performanceTimings.total.toFixed(2)}ms (${(performanceTimings.total / 1000).toFixed(2)}s)`,
          );

          console.log("\n✅ OCR Complete. All Results:");
          ocrResults.forEach((result, index) => {
            console.log(`  ${index + 1}. ${JSON.stringify(result)}`);
          });
          console.log("\n");
        }

        if (enableLogging)
          showNotification({
            type: "success",
            message: `OCR completed successfully. ${performanceTimings.total.toFixed(2)}ms`,
          });

        return ocrResults;
      } catch (err) {
        const overallEndTime = performance.now();
        performanceTimings.total = overallEndTime - overallStartTime;

        // Terminate worker on error
        if (worker) {
          try {
            await worker.terminate();
            workerRef.current = null;
            if (enableLogging) {
              console.log("Tesseract worker terminated after error");
            }
          } catch (terminateError) {
            console.warn("Error terminating Tesseract worker after error:", terminateError);
          }
        }

        if (enableLogging) {
          console.error("\n" + "=".repeat(60));
          console.error("❌ OCR Error:", err);
          console.error(`⏱️  Time before failure: ${performanceTimings.total.toFixed(2)}ms`);
          console.error("=".repeat(60));
        }

        throw err;
      } finally {
        // Always clean up resources
        setIsProcessing(false);
        if (frameCanvas) {
          frameCanvas.remove();
        }
      }
    },
    [processUsingScheduler],
  );

  return (
    <OCRContext.Provider
      value={{
        isProcessing,
        runOCR,
      }}
    >
      {children}
    </OCRContext.Provider>
  );
};

export default OCRProvider;
